import { useTranslation } from 'react-i18next';
import { UseFormRegister, UseFormSetValue, UseFormGetValues } from 'react-hook-form';
import type { InitCompanyDto, UpdateCompanyDto, CompanyLocationDto } from '@monorepo/shared';
import { CompanyLocationForm } from './CompanyLocationForm';

interface FieldArrayMethods {
  fields: Array<{ id: string }>;
  append: (value: CompanyLocationDto) => void;
  remove: (index: number) => void;
}

interface LocationsManagementProps<T extends InitCompanyDto | UpdateCompanyDto> {
  register: UseFormRegister<T>;
  setValue: UseFormSetValue<T>;
  getValues: UseFormGetValues<T>;
  errors: {
    locations?: unknown;
  };
  fieldArray: FieldArrayMethods;
  siren?: string;
  locationsData?: CompanyLocationDto[];
}

export const LocationsManagement = <T extends InitCompanyDto | UpdateCompanyDto>({
  register,
  setValue,
  getValues,
  errors,
  fieldArray,
  siren,
  locationsData,
}: LocationsManagementProps<T>) => {
  const { t } = useTranslation();
  const { fields, append, remove } = fieldArray;

  return (
    <div className="space-y-4">
      <div className="border-b pb-2">
        <h2 className="text-xl font-semibold text-text">{t('company.section.locations')}</h2>
      </div>

      {fields.length === 0 && (
        <p className="text-sm text-secondary">{t('company.location.empty')}</p>
      )}

      {fields.map((field, index) => {
        const locationErrors = errors.locations as
          | Record<
              number,
              {
                siret?: { message?: string };
                address?: Record<string, { message?: string }>;
              }
            >
          | undefined;
        const isMainLocation = locationsData?.[index]?.isMainCompanyLocation || false;
        return (
          <CompanyLocationForm
            key={field.id}
            index={index}
            register={register}
            setValue={setValue}
            getValues={getValues}
            errors={locationErrors?.[index] || {}}
            onRemove={() => remove(index)}
            siren={siren}
            canDelete={!isMainLocation}
          />
        );
      })}

      <button
        type="button"
        onClick={() =>
          append({
            siret: '',
            address: {
              street: '',
              postalCode: '',
              city: '',
              country: '',
              additionalInfo: '',
            },
          })
        }
        className="text-primary hover:text-primary/80 text-sm font-medium"
      >
        {t('company.location.add')}
      </button>
    </div>
  );
};
